import {
    ArrayInput,
    Breadcrumb,
    Create,
    DateInput,
    NumberInput,
    AutocompleteArrayInput,
    SelectInput,
    SimpleForm,
    SimpleFormIterator,
    TextInput,
} from "@/components/admin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CreditCard, User, Landmark, ShieldCheck, Building2, ClipboardList } from "lucide-react";
import { useNotify } from "ra-core";
import { VENDOR_CATEGORIES, SPECIALIZATION_CHOICES, RISK_LEVEL_CHOICES } from "./vendorConstants";
import { VendorLocationInputs } from "./VendorLocationInputs";
import { VendorDocumentsInput } from "./VendorDocumentsInput";
import { Link } from "react-router-dom";

const VendorCreateTitle = () => (
    <>
        <Breadcrumb>
            <Breadcrumb.Item><Link to="/vendors">Vendors</Link></Breadcrumb.Item>
            <Breadcrumb.PageItem>Create</Breadcrumb.PageItem>
        </Breadcrumb>
        <span className="text-2xl font-bold tracking-tight">Onboard New Vendor</span>
    </>
);

export const VendorCreate = () => {
    const notify = useNotify();

    const mutationOptions = {
        onError: (error: any) => {
            const msg: string = error?.message ?? '';
            if (msg.includes('23505') || msg.includes('uidx_vendors_name_lower') || msg.toLowerCase().includes('duplicate key')) {
                notify('A vendor with this name already exists.', { type: 'error' });
            } else {
                notify(msg || 'Failed to create vendor.', { type: 'error' });
            }
        },
    };

    return (
        <Create title={<VendorCreateTitle />} className="p-6" mutationOptions={mutationOptions} disableBreadcrumb>
            <SimpleForm className="max-w-4xl mx-auto">

                <div className="space-y-6 w-full">
                    {/* Basic Information */}
                    <Card>
                        <CardHeader className="pb-3 border-b bg-muted/50">
                            <CardTitle className="text-xs font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                                <Building2 className="w-3.5 h-3.5" /> Company Information
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-6 space-y-4">
                            <TextInput source="name" label="Vendor Name" isRequired className="font-semibold" />
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <SelectInput source="category" label="Category" choices={VENDOR_CATEGORIES} />
                                <TextInput source="material_service" label="Material / Service" />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <TextInput source="payment_terms" label="Payment Terms" />
                                <TextInput source="gst_number" label="GSTIN" />
                            </div>
                            <TextInput source="pan_number" label="PAN Number" />
                        </CardContent>
                    </Card>

                    {/* Primary Contact */}
                    <Card>
                        <CardHeader className="pb-3 border-b bg-muted/50">
                            <CardTitle className="text-xs font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                                <User className="w-3.5 h-3.5" /> Primary Contact Person
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-6 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <TextInput source="contact_person" label="Contact Person" />
                                <TextInput source="designation" label="Designation" />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <TextInput source="email" label="Email Address" />
                                <TextInput source="phone" label="Phone Number" />
                            </div>
                        </CardContent>
                    </Card>

                    {/* Additional Contact Persons */}
                    <Card>
                        <CardHeader className="pb-3 border-b bg-muted/50">
                            <CardTitle className="text-xs font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                                <User className="w-3.5 h-3.5" /> Additional Contact Persons
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-6">
                            <ArrayInput source="contact_persons" label={false} helperText={false}>
                                <SimpleFormIterator inline disableReordering>
                                    <TextInput source="name" label="Name" helperText={false} className="w-full" />
                                    <TextInput source="designation" label="Designation" helperText={false} className="w-full" />
                                    <TextInput source="email" label="Email" helperText={false} className="w-full" />
                                    <TextInput source="phone" label="Phone" helperText={false} className="w-full" />
                                </SimpleFormIterator>
                            </ArrayInput>
                        </CardContent>
                    </Card>

                    {/* Address Information */}
                    <Card>
                        <CardHeader className="pb-3 border-b bg-muted/50">
                            <CardTitle className="text-xs font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                                <Landmark className="w-3.5 h-3.5" /> Office Address
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-6 space-y-4">
                            <TextInput source="address" label="Street Address" multiline rows={2} />
                            <VendorLocationInputs />
                        </CardContent>
                    </Card>

                    {/* Bank Details */}
                    <Card>
                        <CardHeader className="pb-3 border-b bg-muted/50">
                            <CardTitle className="text-xs font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                                <CreditCard className="w-3.5 h-3.5" /> Banking Information
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-6 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <TextInput source="bank_name" label="Bank Name" />
                                <TextInput source="bank_branch" label="Branch Name" />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <TextInput source="bank_account_number" label="Account Number" />
                                <TextInput source="bank_ifsc_code" label="IFSC Code" />
                            </div>
                        </CardContent>
                    </Card>

                    {/* Performance & Specialization */}
                    <Card className="bg-muted/50 border-border">
                        <CardHeader className="pb-3 border-b bg-muted/50">
                            <CardTitle className="text-xs font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                                <ShieldCheck className="w-3.5 h-3.5" /> Specialization & Initial Rating
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-6 space-y-4">
                            <AutocompleteArrayInput
                                source="specialization_tags"
                                label="Specialization Tags"
                                choices={SPECIALIZATION_CHOICES}
                            />
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <NumberInput source="performance_rating" label="Rating (1-5)" min={1} max={5} />
                            </div>
                            <TextInput source="past_performance_notes" label="Initial Performance Notes" multiline rows={2} />
                        </CardContent>
                    </Card>

                    {/* Vendor Evaluation */}
                    <Card className="border-blue-100">
                        <CardHeader className="pb-3 border-b bg-blue-50/40">
                            <CardTitle className="text-xs font-bold uppercase tracking-widest text-blue-700 flex items-center gap-2">
                                <ClipboardList className="w-3.5 h-3.5" /> Vendor Evaluation
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-6 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <NumberInput source="evaluation_score" label="Evaluation Score (%)" min={0} max={100} />
                                <SelectInput source="risk_level" label="Risk Level" choices={RISK_LEVEL_CHOICES} />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <DateInput source="last_evaluation_date" label="Last Evaluation Date" />
                                <DateInput source="next_due_date" label="Next Due Date" />
                            </div>
                            <TextInput source="evaluation_remarks" label="Remarks" multiline rows={2} />
                        </CardContent>
                    </Card>

                    {/* Document Checklist */}
                    <Card>
                        <CardHeader className="pb-3 border-b bg-muted/50">
                            <CardTitle className="text-xs font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                                <ClipboardList className="w-3.5 h-3.5" /> Document Checklist
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-6">
                            <VendorDocumentsInput />
                        </CardContent>
                    </Card>
                </div>
            </SimpleForm>
        </Create>
    );
};
